<script setup>
import { onBeforeUnmount, ref } from "vue"
import { DirectUpload } from "@rails/activestorage"

const props = defineProps({
  directUploadUrl:      { type: String, required: true },
  importUrl:            { type: String, required: true },
  // Rails path with a placeholder swapped at runtime, e.g. /guide_document_imports/IMPORT_KEY
  statusPathTemplate:   { type: String, required: true },
  // Rails path with a placeholder, e.g. /guide_versions/GUIDE_VERSION_ID/edit
  editPathTemplate:     { type: String, required: true },
  // Optional: pre-selected station context forwarded to the import endpoint
  stationId:            { type: String, default: "" },
  dropLabel:            { type: String, default: "Drop a document here or click to browse" },
  acceptedFormatsLabel: { type: String, default: "Accepted: PDF, DOCX, TXT" },
  processingLabel:      { type: String, default: "Analyzing document and generating guide…" },
  errorLabel:           { type: String, default: "Something went wrong. Please try again." },
  tryAgainLabel:        { type: String, default: "Try again" },
})

const ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
].join(",")

const POLL_INTERVAL_MS = 2000
const MAX_POLL_ATTEMPTS = 300

// "idle" | "uploading" | "processing" | "error"
const status         = ref("idle")
const uploadProgress = ref(0)
const errorMessage   = ref("")
const isDragging     = ref(false)

const fileInput = ref(null)
let dragDepth   = 0
let pollTimer   = null
let pollCount   = 0

function logClient(level, event, payload = {}) {
  const logger = console[level] || console.log
  logger("[DocumentDropzone]", event, payload)
}

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.content ?? ""
}

// ── Drag-and-drop handlers ───────────────────────────────────────────────────

function onDragEnter(e) {
  e.preventDefault()
  dragDepth += 1
  isDragging.value = true
}

function onDragOver(e) {
  e.preventDefault()
}

function onDragLeave(e) {
  e.preventDefault()
  dragDepth -= 1
  if (dragDepth <= 0) {
    dragDepth = 0
    isDragging.value = false
  }
}

function onDrop(e) {
  e.preventDefault()
  dragDepth = 0
  isDragging.value = false
  const file = e.dataTransfer?.files?.[0]
  if (file) handleFile(file)
}

function onFileChange(e) {
  const file = e.target.files?.[0]
  if (file) handleFile(file)
}

// ── Upload flow ──────────────────────────────────────────────────────────────

function handleFile(file) {
  logClient("info", "file_selected", { name: file.name, type: file.type, size: file.size })
  status.value         = "uploading"
  uploadProgress.value = 0
  errorMessage.value   = ""

  const upload = new DirectUpload(file, props.directUploadUrl, {
    directUploadWillStoreFileWithXHR(xhr) {
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          uploadProgress.value = Math.round((e.loaded / e.total) * 100)
        }
      })
    },
  })

  upload.create((error, blob) => {
    if (error) {
      logClient("error", "direct_upload_failed", { message: error.message })
      setError(error.message)
      return
    }
    logClient("info", "direct_upload_completed", { blobSignedIdPresent: Boolean(blob?.signed_id) })
    submitImport(blob.signed_id)
  })
}

async function submitImport(signedId) {
  status.value = "processing"
  logClient("info", "import_submit_started")

  try {
    const body = { document_blob_signed_id: signedId }
    if (props.stationId) body.station_id = props.stationId

    const res = await fetch(props.importUrl, {
      method:      "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept:         "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      body: JSON.stringify(body),
    })

    const data = await parseJsonResponse(res)
    if (!res.ok) throw new Error(data.error ?? props.errorLabel)

    logClient("info", "import_submit_completed", { importKey: data.import_key })
    startPolling(data.import_key)
  } catch (err) {
    logClient("error", "import_submit_failed", { message: err.message })
    setError(err.message)
  }
}

// ── Status polling ───────────────────────────────────────────────────────────

function startPolling(importKey) {
  stopPolling()
  pollCount = 0
  const url = props.statusPathTemplate.replace("IMPORT_KEY", importKey)
  logClient("info", "polling_started", { importKey, url })

  pollTimer = setInterval(async () => {
    pollCount += 1
    if (pollCount > MAX_POLL_ATTEMPTS) {
      stopPolling()
      logClient("warn", "polling_timeout", { importKey, pollCount, maxPollAttempts: MAX_POLL_ATTEMPTS })
      setError("Import timed out. Please check worker logs and try again.")
      return
    }

    try {
      const res = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      })
      const data = await parseJsonResponse(res)
      logClient("debug", "polling_tick", { importKey, httpStatus: res.status, status: data.status })

      if (!res.ok) {
        stopPolling()
        logClient("warn", "polling_http_error", { importKey, httpStatus: res.status, error: data.error })
        setError(data.error ?? props.errorLabel)
        return
      }

      if (data.status === "done" && data.guide_version_id) {
        stopPolling()
        logClient("info", "polling_done", { importKey, guideVersionId: data.guide_version_id })
        window.location.href = props.editPathTemplate.replace(
          "GUIDE_VERSION_ID",
          data.guide_version_id
        )
      } else if (data.status === "failed") {
        stopPolling()
        logClient("warn", "polling_failed", { importKey, error: data.error })
        setError(data.error ?? props.errorLabel)
      } else if (!["pending", "processing"].includes(data.status)) {
        stopPolling()
        logClient("warn", "polling_unknown_status", { importKey, status: data.status })
        setError(props.errorLabel)
      }
    } catch (err) {
      stopPolling()
      logClient("error", "polling_exception", { importKey, message: err.message })
      setError(err.message)
    }
  }, POLL_INTERVAL_MS)
}

async function parseJsonResponse(response) {
  const text = await response.text()

  if (!text) {
    return {}
  }

  try {
    return JSON.parse(text)
  } catch {
    throw new Error(props.errorLabel)
  }
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
    pollCount = 0
    logClient("debug", "polling_stopped")
  }
}

function setError(msg) {
  status.value       = "error"
  errorMessage.value = msg ?? props.errorLabel
  logClient("error", "ui_error", { message: errorMessage.value })
}

function reset() {
  stopPolling()
  status.value         = "idle"
  errorMessage.value   = ""
  uploadProgress.value = 0
  if (fileInput.value) fileInput.value.value = ""
}

onBeforeUnmount(stopPolling)
</script>

<template>
  <div class="document-dropzone-wrapper h-100 d-flex flex-column justify-content-center">

    <!-- Idle: drop target -->
    <div
      v-if="status === 'idle'"
      class="document-dropzone border border-2 border-dashed rounded-3 p-4 text-center"
      :class="{ 'document-dropzone--dragging bg-primary-subtle border-primary': isDragging }"
      role="button"
      tabindex="0"
      @click="fileInput.click()"
      @keydown.enter="fileInput.click()"
      @keydown.space.prevent="fileInput.click()"
      @dragenter="onDragEnter"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        fill="currentColor"
        class="text-muted mb-3"
        viewBox="0 0 16 16"
        aria-hidden="true"
      >
        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
        <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708z" />
      </svg>
      <p class="fw-medium mb-1">{{ dropLabel }}</p>
      <p class="text-muted small mb-0">{{ acceptedFormatsLabel }}</p>
      <input
        ref="fileInput"
        type="file"
        :accept="ACCEPTED_MIME_TYPES"
        class="d-none"
        @change="onFileChange"
      />
    </div>

    <!-- Uploading: progress bar -->
    <div v-else-if="status === 'uploading'" class="text-center py-4 px-3">
      <div class="progress mb-3" style="height: 8px;">
        <div
          class="progress-bar progress-bar-striped progress-bar-animated"
          role="progressbar"
          :style="{ width: uploadProgress + '%' }"
          :aria-valuenow="uploadProgress"
          aria-valuemin="0"
          aria-valuemax="100"
        />
      </div>
      <p class="text-muted small mb-0">{{ uploadProgress }}%</p>
    </div>

    <!-- Processing: spinner with notification message -->
    <div v-else-if="status === 'processing'" class="text-center py-4 px-3">
      <div class="spinner-border text-primary mb-3" role="status">
        <span class="visually-hidden">Processing…</span>
      </div>
      <p class="text-muted mb-0">{{ processingLabel }}</p>
    </div>

    <!-- Error state -->
    <div v-else-if="status === 'error'" class="alert alert-danger mb-0">
      <p class="mb-2 small">{{ errorMessage }}</p>
      <button type="button" class="btn btn-sm btn-outline-secondary" @click="reset">
        {{ tryAgainLabel }}
      </button>
    </div>

  </div>
</template>
